import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { FetchAllBrands, FetchAllBrandsSuccess } from 'src/app/features/brands/store/brands.actions';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { FetchAllCategories, FetchAllCategoriesSuccess } from 'src/app/features/categories/store/categories.actions';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { parseCompanyFilterRequest, refillCompanyFilter } from 'src/app/shared/utils/helpers';
import { ICompanyFilter, IPayment_term } from '../../models/customers.models';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { FetchAllpaymentTerms, FetchAllpaymentTermsSuccess } from '../../store/customers.actions';
import { FetchCompaniesGroups, FetchCompaniesGroupsSuccess } from 'src/app/features/companies-groups/store/companies-groups.actions';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Actions, ofType } from '@ngrx/effects';



interface BooleanFilterOption {
  label: string;
  value: boolean | null;
}

@Component({
  selector: 'app-customers-filters',
  templateUrl: './customers-filters.component.html',
  styleUrls: ['./customers-filters.component.scss']
})
export class CustomersFiltersComponent implements OnInit {

  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  currentFilter: Observable<ICompanyFilter> = this.store.select(
    (state) => state.customers.companyFilterState.advanced
  );
  companyFilters = LocalStorageService.get('companyFilters');

  brands$: Observable<IBrand[]> = this.store.select(
    (state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name))
  );
  filteredBrands$: Observable<IBrand[]> = this.brands$;
  brandsFilterKey: string = '';

  categories$ = this.store.select((state) => [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)));
  filteredCategories$ = this.categories$;
  categoriesFilterKey: string = '';

  paymentTerms$: Observable<IPayment_term[]> = this.store.select((state) => [...state.customers.allPaymentTerms].sort((a, b) => a.name.localeCompare(b.name)));
  paymentFilterKey: string = '';
  filteredPayments$: Observable<IPayment_term[]> = this.paymentTerms$;

  groups$: Observable<ICompaniesGroups[]> = this.store.select(
    (state) => [...state.companiesGroups.AllCompaniesGroups].sort((a, b) => a.name.localeCompare(b.name))
  );
  filteredGroups$: Observable<ICompaniesGroups[]> = this.groups$;
  groupsFilterKey: string = '';


  has_billing: { label: string; value: boolean | null }[] = [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ];

  shipping_term: { label: string; value: boolean | null }[] = [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ];

  selectedGroup = 'company_info';

  filter: ICompanyFilter = refillCompanyFilter()
  filterName = '';
  toggleSaveFilterForm = false;
  filterNameControl = new FormControl('', Validators.required);

  isLocation: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CustomersFiltersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private actions$: Actions,
  ) { }

  ngOnInit(): void {
    this.currentFilter.subscribe((filter: ICompanyFilter) => {
      this.filter = refillCompanyFilter(filter);
    });

    if(this.data?.isLocation) {
      this.isLocation = true;
      this.selectedGroup = 'location_info'
    }

    this.store.dispatch(FetchAllBrands({}));
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchAllpaymentTerms());
    this.store.dispatch(FetchCompaniesGroups({includes:['id','name']}));
    this.actions$.pipe(
      ofType(FetchAllBrandsSuccess, FetchAllCategoriesSuccess, FetchAllpaymentTermsSuccess, FetchCompaniesGroupsSuccess)
    ).subscribe(action => {
      switch (action.type) {
        case FetchAllBrandsSuccess.type:
          this.filterBrands();
          break;

        case FetchAllCategoriesSuccess.type:
          this.filterCategories();
          break;

        case FetchAllpaymentTermsSuccess.type:
          this.filterPayment();
          break;

        case FetchCompaniesGroupsSuccess.type:
          this.filterGroups();
          break;
      }
    });
  }

  getFiltersCount(): number {
    return parseCompanyFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters(): void {
    this.filter = refillCompanyFilter();
    this.filterName = '';
  }

  getPaymentTerms(): string {
    return (this.filter?.locationInfoFilter.payment_terms || [])
      .map((p) => p.name)
      .join(', ');
  }

  filterPayment(): void {
    this.filteredPayments$ = this.paymentTerms$.pipe(
      map((payments) =>
        payments.filter(
          (payment) =>
            payment.name
              .toLocaleLowerCase()
              ?.includes(this.paymentFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.locationInfoFilter.payment_terms?.find(
              (p) => p.id === payment.id
            )
        )
      )
    );
  }

  getPaymentNames(): string {
    return (this.filter?.locationInfoFilter.payment_terms || [])
      .map((payment) => payment.name)
      .join(', ');
  }
  filterBrands(): void {
    this.filteredBrands$ = this.brands$.pipe(
      map((brands) =>
        brands.filter(
          (brand) =>
            brand.name
              .toLocaleLowerCase()
              ?.includes(this.brandsFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.organizeFilter?.fav_brands?.find(
              (b) => b.id === brand.id
            )
        )
      )
    );
  }

  selectOption(type: string, event: MatAutocompleteSelectedEvent){
    switch (type) {
      case 'brand':
        const brand: IBrand = event.option.value;
        if (this.filter?.organizeFilter.fav_brands)
          this.filter?.organizeFilter.fav_brands.push(brand)
        setTimeout(() => {
          this.brandsFilterKey = '';
          this.filterBrands();
        });
        break;
      case 'category':
        const category: ICategory = event.option.value
        this.filter?.organizeFilter?.fav_categories?.push(category);
        setTimeout(() => {
          this.categoriesFilterKey = '';
          this.filterCategories();
        });
        break;
      case 'payment':
        const payment: IPayment_term = event.option.value
        if (this.filter?.locationInfoFilter.payment_terms)
          this.filter?.locationInfoFilter.payment_terms?.push(payment);
        setTimeout(() => {
          this.paymentFilterKey = '';
          this.filterPayment()
        });
        break;
      case 'group':
        const group: ICompaniesGroups = event.option.value
        if (this.filter?.organizeFilter.groups)
          this.filter?.organizeFilter.groups?.push(group);
        setTimeout(() => {
          this.groupsFilterKey = '';
          this.filterGroups()
        });
        break;
    }
  }

  getBrandsNames(): string {
    return (this.filter?.organizeFilter.fav_brands || [])
      .map((brand) => brand.name)
      .join(', ');
  }

  filterCategories(): void {
    this.filteredCategories$ = this.categories$.pipe(
      map((categories) =>
        categories.filter(
          (category) =>
            category.name
              .toLocaleLowerCase()
              ?.includes(this.categoriesFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.organizeFilter?.fav_categories.find(
              (c) => c.id === category.id
            )
        )
      )
    );
  }

  getCategoriesNames(): string {
    return (this.filter?.organizeFilter?.fav_categories || [])
      .map((category) => category.name)
      .join(', ');
  }

  filterGroups(): void {
    this.filteredGroups$ = this.groups$.pipe(
      map((groups) =>
        groups.filter(
          (group) =>
            group.name
              .toLocaleLowerCase()
              ?.includes(this.groupsFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.organizeFilter.groups?.find(
              (g) => g.id === group.id
            )
        )
      )
    );
  }

  getGroupsNames(): string {
    return (this.filter?.organizeFilter.groups || [])
      .map((group) => group.name)
      .join(', ');
  }

  toggleSaveFilter(): void {
    this.toggleSaveFilterForm = !this.toggleSaveFilterForm;
  }

  save(): boolean | void {
    if(!this.filterNameControl.valid){
      this.filterNameControl.markAsTouched();
      return false
    }
    if (!this.companyFilters?.filters)
      this.companyFilters = {
        filters: [{ name: this.filterName, filter: this.filter }],
      };
    else {
      const filter = this.companyFilters.filters?.find(
        (f: any) => f.name.toLowerCase() === this.filterName.toLowerCase()
      );
      if (filter)
        this.companyFilters.filters = this.companyFilters.filters?.map((f: any) => {
          if (f.name === this.filterName) return { name: this.filterName, filter: this.filter };
          return f;
        });
      else
        this.companyFilters.filters.push({
          name: this.filterName,
          filter: this.filter,
        });
    }
    LocalStorageService.put('companyFilters', this.companyFilters);
    this.toggleSaveFilter();
  }

  setFilter(filter: any): void {
    if(filter){
      this.filterName = filter.name;
      this.filter = refillCompanyFilter(filter.filter);
    }
  }


  deleteFilter(filter: any): void {
    this.companyFilters.filters = this.companyFilters.filters?.filter(
      (f: any) => f.name !== filter.name
    );
    LocalStorageService.put('companyFilters', this.companyFilters);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }
}
